/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { autorun } from 'mobx';
import * as React from 'react';

/**
 * A hook used for bridging the gap between mobx observables
 * and React's vanilla state.
 * It uses autorun to subscribe to observables captured by the init function,
 * and sets the react state to trigger a rerender.
 * The somewhat naive approach to this is to run the init function inside
 * an observer component, in which case every render will generate a new reference
 * from running the init function which is undesirable in big components.
 */
export const useAutorunState = <T>(init: () => T, initialVal: T) => {
    const [data, setData] = React.useState<T>(initialVal);

    React.useEffect(
        () =>
            autorun(() => {
                setData(init());
            }),
        [init]
    );

    return data;
};
